import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser, useClerk } from "@clerk/clerk-react";
import { api } from "../api/client";
import { websiteUrl } from "../lib/websiteUrl";
import FeedbackModal from "./FeedbackModal";
import type { Usage, Me } from "../types";

const PLAN_LABEL: Record<string, string> = { trial: "Trial", pratica: "Pratica", maestro: "Maestro" };

export default function ProfileMenu() {
  const { user } = useUser();
  const { signOut, openUserProfile } = useClerk();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.getUsage().then(setUsage).catch(() => {});
    api.getMe().then(setMe).catch(() => { /* admin item stays hidden */ });
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const avatarUrl = user?.imageUrl;
  const name = user?.fullName ?? user?.username ?? "Account";

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center cursor-pointer ring-1 ring-border-soft hover:ring-border transition-all"
        aria-label="Profile menu"
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt={name} className="w-full h-full object-cover" />
        ) : (
          <span className="text-xs font-semibold text-text-secondary bg-surface2 w-full h-full flex items-center justify-center">
            {name.charAt(0).toUpperCase()}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-10 z-50 w-56 rounded-[14px] border border-border bg-surface overflow-hidden"
          style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.6)" }}
        >
          <div className="px-4 py-3 border-b border-border-soft">
            <p className="text-sm font-semibold text-ink truncate">{name}</p>
            {user?.primaryEmailAddress?.emailAddress && (
              <p className="text-xs text-text-tertiary truncate">{user.primaryEmailAddress.emailAddress}</p>
            )}
            {usage && (
              <div className="flex items-start gap-3 mt-2">
                <div className="flex flex-col gap-0.5">
                  <span className="text-[0.6rem] uppercase tracking-wider text-text-tertiary font-medium">Plan</span>
                  <span className={`px-2 py-0.5 rounded-full text-[0.6875rem] font-semibold uppercase tracking-wide w-fit ${
                    usage.blocked ? "bg-rose-500/20 text-rose-400" : "bg-success/20 text-success"
                  }`}>
                    {PLAN_LABEL[usage.plan] ?? usage.plan}
                  </span>
                </div>
                <div className="flex flex-col gap-0">
                  <span className="text-[0.6rem] uppercase tracking-wider text-text-tertiary font-medium">Sessions</span>
                  <span className="text-[0.8125rem] font-semibold text-ink whitespace-nowrap leading-tight">
                    {`${usage.remaining}/${usage.limit} left`}
                  </span>
                  {usage.pack_credits ? (
                    <span className="text-[0.8125rem] text-success font-medium whitespace-nowrap leading-tight">+{usage.pack_credits} pack</span>
                  ) : null}
                </div>
              </div>
            )}
          </div>
          <div className="py-1">
            {/* Library isn't a core destination on the nav bar or tab bar,
                so it surfaces here on both desktop and mobile. */}
            <button
              onClick={() => { setOpen(false); navigate("/knowledge"); }}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-text hover:bg-surface2 hover:text-ink transition-colors cursor-pointer"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
              </svg>
              Library
            </button>
            <button
              onClick={() => { setOpen(false); navigate("/settings"); }}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-text hover:bg-surface2 hover:text-ink transition-colors cursor-pointer"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
              Settings
            </button>
          </div>
          <div className="border-t border-border-soft py-1">
            {usage && usage.plan === "trial" && (
              <div className="px-2 pt-1.5 pb-1">
                <button
                  onClick={() => { setOpen(false); navigate("/upgrade"); }}
                  className="group w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-[10px] text-sm font-semibold text-amber-950 cursor-pointer transition-all hover:brightness-105 active:scale-[0.98]"
                  style={{
                    background: "linear-gradient(135deg, #f5c451 0%, #e8a93c 100%)",
                    boxShadow: "0 2px 10px rgba(232, 169, 60, 0.35)",
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none" className="transition-transform group-hover:scale-110">
                    <path d="M12 2l2.2 5.8L20 10l-5.8 2.2L12 18l-2.2-5.8L4 10l5.8-2.2z" />
                  </svg>
                  Upgrade plan
                </button>
              </div>
            )}
            {usage && (usage.plan === "pratica" || usage.plan === "maestro") && (
              <button
                onClick={() => { setOpen(false); navigate("/upgrade"); }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-text hover:bg-surface2 hover:text-ink transition-colors cursor-pointer"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
                Manage plan
              </button>
            )}
            {openUserProfile && (
              <button
                onClick={() => { setOpen(false); openUserProfile(); }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-text hover:bg-surface2 hover:text-ink transition-colors cursor-pointer"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                Manage account
              </button>
            )}
          </div>
          <div className="border-t border-border-soft py-1">
            <button
              onClick={() => { setOpen(false); setFeedbackOpen(true); }}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-text hover:bg-surface2 hover:text-ink transition-colors cursor-pointer"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              Send feedback
            </button>
          </div>
          {me?.is_admin && (
            <div className="border-t border-border-soft py-1">
              <button
                onClick={() => { setOpen(false); navigate("/admin"); }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-text hover:bg-surface2 hover:text-ink transition-colors cursor-pointer"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                Admin
              </button>
            </div>
          )}
          <div className="border-t border-border-soft py-1">
            <button
              onClick={() => signOut({ redirectUrl: websiteUrl() })}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-text hover:bg-surface2 hover:text-ink transition-colors cursor-pointer"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Sign out
            </button>
          </div>
        </div>
      )}

      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}
    </div>
  );
}
